import React from 'react';
import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '../ui/card';

interface MetricCardProps {
  title: string;
  value: number | string;
  trend?: {
    value: number;
    label: string;
    direction: 'up' | 'down' | 'neutral';
  };
  icon: LucideIcon;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo';
  actionButton?: string;
  onClick?: () => void;
}

const colorClasses = {
  blue: {
    icon: 'text-blue-500 bg-blue-100',
    trend: 'text-blue-600',
    button: 'text-blue-700 hover:text-blue-900'
  },
  green: {
    icon: 'text-green-500 bg-green-100',
    trend: 'text-green-600', 
    button: 'text-green-700 hover:text-green-900'
  },
  yellow: {
    icon: 'text-yellow-500 bg-yellow-100',
    trend: 'text-yellow-600',
    button: 'text-yellow-700 hover:text-yellow-900'
  },
  red: {
    icon: 'text-red-500 bg-red-100',
    trend: 'text-red-600',
    button: 'text-red-700 hover:text-red-900'
  },
  purple: {
    icon: 'text-purple-500 bg-purple-100',
    trend: 'text-purple-600',
    button: 'text-purple-700 hover:text-purple-900'
  },
  indigo: {
    icon: 'text-indigo-500 bg-indigo-100',
    trend: 'text-indigo-600',
    button: 'text-indigo-700 hover:text-indigo-900'
  }
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  trend,
  icon: Icon,
  color = 'blue',
  actionButton,
  onClick
}) => {
  const colors = colorClasses[color];

  const getTrendIcon = () => {
    switch (trend?.direction) {
      case 'up':
        return <TrendingUp className="w-3 h-3" />;
      case 'down':
        return <TrendingDown className="w-3 h-3" />;
      default:
        return <Minus className="w-3 h-3" />;
    }
  };

  const getTrendColor = () => {
    switch (trend?.direction) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <Card className="overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200 border-0 rounded-xl">
      <div className="p-6">
        {/* Header with Icon */}
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${colors.icon}`}>
            <Icon className="w-6 h-6" />
          </div>
          {trend && (
            <div className={`flex items-center space-x-1 ${getTrendColor()}`}>
              {getTrendIcon()}
              <span className="text-sm font-medium">
                {Math.abs(trend.value)}%
              </span>
            </div>
          )}
        </div>

        {/* Metric Value */}
        <div className="mb-2">
          <div className="text-2xl font-bold text-gray-900">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          <div className="text-sm font-medium text-gray-500 truncate">
            {title}
          </div>
        </div>

        {/* Trend Label */}
        {trend && (
          <div className="text-xs text-gray-500 mb-4">
            {trend.label}
          </div>
        )}

        {/* Action Button */}
        {actionButton && onClick && (
          <button
            onClick={onClick}
            className={`text-sm font-medium ${colors.button} transition-colors hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
          >
            {actionButton} →
          </button>
        )}
      </div>
    </Card>
  );
}; 